import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Typography,
    Paper,
    Grid,
} from "@mui/material";
import { HomeOutlined } from "@mui/icons-material";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import * as wjGrid from "@grapecity/wijmo.react.grid";
import * as wjCore from "@grapecity/wijmo";
import "@grapecity/wijmo.styles/wijmo.css";
import ApartmentSelectionDialog from "./ApartmentSelectionDialog";
import {
    PeopleAltOutlined,
    EventSeatOutlined,
    PendingActionsOutlined,
    Groups2Outlined,
} from "@mui/icons-material";
import { getAgeAtYearEnd } from "qubCommon/helperMethods/genericMethods";


const ApartmentInfoTile = ({ label, value, color, icon }) => (
    <Box
        sx={{
            flex: "1 1 45%",
            bgcolor: "#fafafa",
            borderRadius: 2,
            p: 1.5,
            border: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            transition: "all 0.2s ease",
            "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                transform: "translateY(-2px)",
            },
        }}
    >
        <Box
            sx={{
                bgcolor: color,
                borderRadius: "10px",
                p: 0.8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
            }}
        >
            {icon}
        </Box>

        <Box>
            <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "#424242", lineHeight: 1 }}
            >
                {label}
            </Typography>

            <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#212121", lineHeight: 1.2 }}
            >
                {value}
            </Typography>
        </Box>
    </Box>
);


const ApartmentSelection = ({
    apartments = [],
    canChangeApartment = false,
    child,
    openSelectionDialog = false,
    onSelectionDialogClose,
    onSelectionConfirm,
}) => {
    const { watch, setValue } = useFormContext();
    const selected = watch("apartment");
    console.log()


    useEffect(() => {
        if (selected) {
            const selectedApartment = apartments.find(a => a.id === selected.id);
            if (selectedApartment) {
                setValue('apartment', selectedApartment);
            }

            setValue('selectedApartmentId', selected?.id || null);
        }
    }, [])

    const [openDialog, setOpenDialog] = useState(false);


    const formatCharacteristics = (cc) => {
        if (!cc || cc.length === 0) return "-";
        return cc.map((c) => `${c.gender} (${c.age})`).join(", ");
    };

    /** -------------------------------
     *  ACCORDION TITLE
     *  ------------------------------- */
    const AccordionTitle = () => (
        <Stack direction="row" gap={1} alignItems="center">
            <HomeOutlined color="primary" fontSize="small" />

            <Typography color="primary" sx={{ fontWeight: 900 }}>
                {canChangeApartment ? "ΕΠΙΛΟΓΗ ΔΙΑΜΕΡΙΣΜΑΤΟΣ" : "ΔΙΑΜΕΡΙΣΜΑ ΜΗΔ"}
            </Typography>

            {selected && (
                <Typography color="primary" sx={{ ml: 2 }}>
                    {selected?.name}
                </Typography>
            )}
        </Stack>
    );

    /** -------------------------------
     *  ACCORDION MAIN CONTENT
     *  ------------------------------- */
    const AccordionContent = () => (
        <Box>
            {!selected ? (
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Δεν έχει επιλεγεί διαμέρισμα.
                </Typography>
            ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2, mb: 2 }}>

                    <ApartmentInfoTile
                        label="Δυναμικότητα"
                        value={selected.capacity}
                        color="#E3F2FD"
                        icon={<PeopleAltOutlined sx={{ color: "#1E88E5" }} />}
                    />

                    <ApartmentInfoTile
                        label="Διαθέσιμες Θέσεις"
                        value={selected.available ?? selected.availability}
                        color="#E8F5E9"
                        icon={<EventSeatOutlined sx={{ color: "#43A047" }} />}
                    />

                    <ApartmentInfoTile
                        label="Εκκρεμείς Αιτήσεις"
                        value={selected.pendingRequests ?? selected.numOfPendingApps}
                        color="#FFF3E0"
                        icon={<PendingActionsOutlined sx={{ color: "#FB8C00" }} />}
                    />

                    <ApartmentInfoTile
                        label="Ήδη διαμένοντες"
                        value={formatCharacteristics(selected.childrenCharacteristics)}
                        color="#F3E5F5"
                        icon={<Groups2Outlined sx={{ color: "#8E24AA" }} />}
                    />

                </Box>

            )}

            {canChangeApartment && <Button variant="contained" onClick={() => {
                setOpenDialog(true);
            }}>
                {selected ? "Αλλαγή Διαμερίσματος" : "Επιλογή Διαμερίσματος"}
            </Button>}

            {/* ========================================= */}
            {/* ===============   DIALOG   =============== */}
            {/* ========================================= */}

            <ApartmentSelectionDialog
                open={openDialog || openSelectionDialog}
                childGender={child?.gender?.name}
                childAge={getAgeAtYearEnd(child?.dob)}
                apartments={apartments.map(a => a.childrenCharacteristics?.length > 0 ? { ...a, characteristics: formatCharacteristics(a.childrenCharacteristics) } : a)}
                onConfirm={(apt) => {
                    setValue("apartment", apt, { shouldDirty: true });
                    setValue("selectedApartmentId", apt.id, { shouldDirty: true });
                    setOpenDialog(false);
                    onSelectionConfirm?.(apt);
                }}
                onClose={() => {
                    setOpenDialog(false);
                    onSelectionDialogClose?.();
                }}
            />

        </Box>
    );

    return (
        <CustomSimpleAccordion title={<AccordionTitle />} content={<AccordionContent />} />
    );
};

export default ApartmentSelection;
